import React, { useRef, useState } from 'react';
import { colors, getRgbaColor } from '@web-for-marketing/react-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, A11y } from 'swiper/modules';
import 'swiper/css';
import type { Swiper as SwiperType } from 'swiper';
import { IconButton } from '@mui/material';
import { useAtom } from 'jotai';
import { isMobileAtom } from '@atoms/appSettings';
import { CtaBannerCarouselFormContent } from '@models/column';
import { CarouselDotTabs } from '@components/BlogSection/BlogComponents/Home/CarouselDotTabs';
import { RightChevronIconRound } from '@components/IconComponents/RightChevronIconRound';
import { CtaBanner } from './CtaBanner';
import { ctaBannerCarouselStyles as classes } from './styleObjects/CtaBannerCarouselSyles';

export interface CtaBannerCarouselProps {
    content: CtaBannerCarouselFormContent;
    lazyLoad?: boolean;
}

export function CtaBannerCarousel({ content, lazyLoad }: CtaBannerCarouselProps): React.ReactElement {
    const [isMobile] = useAtom(isMobileAtom);
    const [activeBanner, setActiveBanner] = useState(0);
    const swiperRef = useRef<SwiperType | null>(null);
    const trackingClass = content.trackingClass;

    if (activeBanner > content.banners.length - 1) {
        setActiveBanner((prevBanner) => prevBanner - 1);
    }

    const handleBannerChange = (bannerIndex: number): void => {
        setActiveBanner(bannerIndex);
        swiperRef.current?.slideTo(bannerIndex);
    };

    function handleSlideChange(swiper: SwiperType): void {
        setActiveBanner(swiper.activeIndex);
    }

    return (
        <div css={classes.parentContainer}>
            <Swiper
                modules={[Keyboard, A11y]}
                onBeforeInit={(swiper) => {
                    swiperRef.current = swiper;
                }}
                initialSlide={activeBanner}
                onSlideChange={handleSlideChange}
                keyboard={{
                    enabled: true,
                }}
                a11y={{}}
                speed={500}
                slidesPerView={1}
                allowTouchMove={true}
                css={classes.swipeViewsContainerParent}
                style={isMobile ? classes.swipeViewsContainer : undefined}
            >
                {content.banners.map((banner, index) => (
                    <SwiperSlide
                        key={`${index}-${banner.imagePath}`}
                        virtualIndex={index}
                        data-testid='CtaBannerTestID'
                    >
                        <div css={classes.bannerContainer(content.height)}>
                            <CtaBanner
                                banner={banner}
                                lazyLoad={lazyLoad}
                                isHidden={index !== activeBanner}
                                trackingClass={trackingClass}
                            />
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            {!isMobile && content.banners.length > 1 ? (
                <div css={classes.paginationControls}>
                    <CarouselDotTabs
                        maxDots={content.banners.length}
                        activeDot={activeBanner}
                        onChangeSlide={handleBannerChange}
                        dotColor={getRgbaColor(colors.core.snow, 0.4)}
                        activeDotColor={colors.core.snow}
                    />
                    <div css={classes.paginationButtons}>
                        <IconButton
                            size='small'
                            aria-label='Go to previous banner'
                            css={classes.iconButton}
                            onClick={() => handleBannerChange(activeBanner - 1)}
                            disabled={activeBanner === 0}
                        >
                            <RightChevronIconRound css={classes.leftChevronIcon} />
                        </IconButton>
                        <IconButton
                            size='small'
                            aria-label='Go to next banner'
                            css={classes.iconButton}
                            onClick={() => handleBannerChange(activeBanner + 1)}
                            disabled={activeBanner === content.banners.length - 1}
                        >
                            <RightChevronIconRound />
                        </IconButton>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
